// src/components/ChatMessageItem.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import RelativeTime from '@/components/RelativeTime';
import MessageContent from '@/components/MessageContent';
import QuartoExportButton, { ExportFormat } from '@/components/QuartoExportButton';
import QuartoExportLinks, { ExportLinksManager, DownloadInfo } from '@/components/QuartoExportLinks';
import StructuredOutputDisplay from '@/components/StructuredOutputDisplay';
import type { ChatMessage } from '@/types';
import { User } from 'firebase/auth'; // Import User
import { ChevronDown, ChevronUp, Brain } from 'lucide-react';
import { extractThinkingContent } from '@/utils/thinkingExtractor';
import { MessageFeedback } from '@/components/MessageFeedback';
import { MessageFeedbackDialog } from '@/components/MessageFeedbackDialog';
import { MessageProvider } from '@/contexts/MessageContext';
import Link from 'next/link';

// Interface for component props
export interface ChatMessageItemProps {
  message: ChatMessage;
  isUserMessage: boolean;
  avatar: string;
  name: string;
  initials: string;
  enableExport?: boolean;
  currentUser: User; // The currently logged-in user object
  assistantId: string; // ID of the assistant/chat context
  isCurrentlyStreaming?: boolean; // Add this prop
  thinkingContent?: string; // Add new prop for thinking content
  traceId?: string; // Add traceId for Langfuse feedback
  isLastMessage?: boolean; // Add prop to indicate if this is the last message
}

// Memoized ChatMessageItem component for performance optimization
const ChatMessageItem = React.memo(function ChatMessageItem({
  message,
  isUserMessage,
  avatar,
  name,
  initials,
  currentUser, // Destructure currentUser from props
  enableExport = true, // Default export to enabled
  assistantId, // Destructure assistantId from props
  isCurrentlyStreaming = false,
  thinkingContent = '', // Default to empty string
  traceId, // Destructure traceId from props
  isLastMessage = false // Destructure isLastMessage from props
}: ChatMessageItemProps) {
  // Debug logging for structured output
  console.log('ChatMessageItem rendered, message.structuredOutput:', message.structuredOutput ? 'YES' : 'NO');
  if (message.structuredOutput) {
    console.log('ChatMessageItem received structuredOutput:', message.structuredOutput);
  }
  
  // State to manage the visibility of the export panel
  const [isExportOpen, setIsExportOpen] = useState(false);
  // State to manage the currently selected export format, including 'pptx'
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('docx'); // Use ExportFormat type
  // State to manage visibility of thinking content
  const [showThinking, setShowThinking] = useState(false);

  // Determine if the current message can be exported
  // Only non-user messages with actual content are exportable
  const canExport = enableExport &&
    !isUserMessage &&
    message.content &&
    message.content.trim() !== '';

  // Generate a consistent message ID for tracking exports and linking
  // Uses btoa for a simple hash-like ID, removing padding characters.
  // Safely encode Unicode characters to prevent btoa errors
  const safeContent = encodeURIComponent(message.content.substring(0, 50));
  const messageId = message.id || `msg-${btoa(safeContent).replace(/=/g, '')}-${message.timestamp}`;

  // Handler to update the selected export format
  const handleFormatChange = (format: ExportFormat) => { // Use ExportFormat type
    setSelectedFormat(format);
  };

  // Handler to toggle the visibility of the export panel
  const toggleExport = () => {
    setIsExportOpen(prev => !prev);
  };

  // Handler to toggle thinking content visibility
  const toggleThinking = () => {
    setShowThinking(prev => !prev);
  };

  // Handler called when an export is successfully completed
  const handleExportComplete = (msgId: string, downloadInfo: DownloadInfo) => {
    // Add the download link information to the central manager
    ExportLinksManager.addExport(msgId, downloadInfo);
    // Optionally close the export panel after success
    // setIsExportOpen(false); // Keep it open or close based on UX preference
  };

  // Extract thinking content from the message if it's not already provided
  const { cleanContent, extractedThinking } = useMemo(() => {
    // If streaming is active, use the provided thinking content
    if (isCurrentlyStreaming) {
      return { cleanContent: message.content, extractedThinking: thinkingContent };
    }

    // For historical messages, we should have already saved thinkingContent separately
    // Only try to extract if thinkingContent wasn't provided (fallback for older messages)
    if (thinkingContent) {
      // Use the saved thinking content and clean content from message
      return { cleanContent: message.content, extractedThinking: thinkingContent };
    }

    // Fallback: try to extract thinking content from the message content (for older messages)
    return extractThinkingContent(message.content);
  }, [message.content, thinkingContent, isCurrentlyStreaming]);

  // Use either the provided thinking content or the extracted thinking content
  const effectiveThinkingContent = thinkingContent || extractedThinking;

  // Check if we have thinking content and should show the thinking toggle
  const hasThinkingContent = Boolean(effectiveThinkingContent && effectiveThinkingContent.trim() !== '');

  return (
    <div className="mb-4 last:mb-0 group"> {/* Container for the entire message item with group class */}
      <div className={`flex flex-col ${isUserMessage ? 'md:items-end' : 'md:items-start'} items-stretch`}> {/* Center on mobile, align on desktop */}
        {/* Avatar and Metadata Row */}
        <div className={`flex items-center gap-2 mb-1 ${isUserMessage ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Avatar */}
          {!isUserMessage ? (
            <Link href={`/assistant/${assistantId}`} className="flex-shrink-0">
              <Avatar className="h-8 w-8 hover:ring-2 hover:ring-primary transition-all cursor-pointer">
                {avatar ? (
                  <AvatarImage src={avatar} alt={name} referrerPolicy="no-referrer" />
                ) : (
                  <AvatarFallback className="text-sm">{initials}</AvatarFallback>
                )}
              </Avatar>
            </Link>
          ) : (
            <Avatar className="h-8 w-8">
              {avatar ? (
                <AvatarImage src={avatar} alt={name} referrerPolicy="no-referrer" />
              ) : (
                <AvatarFallback className="text-sm">{initials}</AvatarFallback>
              )}
            </Avatar>
          )}
          {/* Sender Name and Timestamp */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-medium">{name}</span>
            <RelativeTime timestamp={message.timestamp || Date.now()} />
          </div>
        </div>

        {/* Message Content Box */}
        <div className={`max-w-full md:max-w-[85%] overflow-hidden ${isUserMessage ? 'md:ml-auto' : 'md:mr-auto'} min-w-0`}> {/* Full width on mobile, limit on desktop */}
          <MessageProvider isLastMessage={isLastMessage}>
            <MessageContent
              content={cleanContent}
              isUser={isUserMessage}
              role={message.sender}
              streaming={true}
              streamSpeed={50}
              isCurrentlyStreaming={isCurrentlyStreaming}
            />
          </MessageProvider>
        </div>

        {/* Action buttons row - Only show for non-user messages */}
        {!isUserMessage && (traceId || hasThinkingContent || canExport) && (
          <div className={`mt-1 flex items-center gap-2 w-full ${isUserMessage ? 'pl-[15%]' : 'pr-[15%]'} ${isUserMessage ? 'justify-end' : 'justify-start'}`}>
            {/* Feedback buttons */}
            {traceId && (
              <>
                <MessageFeedback 
                  traceId={traceId} 
                  messageId={message.id}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                />
                <MessageFeedbackDialog 
                  traceId={traceId} 
                  messageId={message.id}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                />
              </>
            )}
            
            {/* Thinking content toggle */}
            {hasThinkingContent && (
              <button
                onClick={toggleThinking}
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent rounded-md h-6 px-2 text-xs hover:text-foreground opacity-0 group-hover:opacity-100"
                style={{
                  // Gradient text when active, otherwise muted
                  color: showThinking ? 'transparent' : 'var(--muted-foreground)',
                  background: showThinking ? 
                    'linear-gradient(90deg, var(--primary) 0%, var(--primary-lighter, #7c78ff) 100%)' : 
                    'transparent',
                  backgroundClip: showThinking ? 'text' : 'border-box',
                  WebkitBackgroundClip: showThinking ? 'text' : 'border-box'
                }}
                aria-expanded={showThinking}
                aria-controls={`thinking-panel-${messageId}`}
                aria-label="Toggle thinking process"
              >
                <Brain 
                  className="h-3.5 w-3.5 mr-0.5" 
                  style={{
                    color: showThinking ? 'var(--primary)' : 'var(--muted-foreground)'
                  }}
                />
                {showThinking ? "Hide thinking" : "Show thinking"}
                {/* Chevron icon indicating open/closed state */}
                {showThinking ? (
                  <ChevronUp className="h-3 w-3 ml-0.5" />
                ) : (
                  <ChevronDown className="h-3 w-3 ml-0.5" />
                )}
              </button>
            )}
            
            {/* Export Toggle Button */}
            {canExport && (
              <button
                onClick={toggleExport}
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0 hover:bg-accent rounded-md h-6 px-2 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100"
                aria-expanded={isExportOpen}
                aria-controls={`export-panel-${messageId}`}
                aria-label="Toggle export options"
              >
                Export
                {/* Chevron icon indicating open/closed state */}
                {isExportOpen ? (
                  <ChevronUp className="ml-1 h-3 w-3" />
                ) : (
                  <ChevronDown className="ml-1 h-3 w-3" />
                )}
              </button>
            )}
          </div>
        )}

        {/* Thinking content panel */}
        {hasThinkingContent && showThinking && (
          <div
            id={`thinking-panel-${messageId}`}
            className={`mt-2 w-full ${isUserMessage ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}
          >
            <div className="opacity-90">
              <div className="prose prose-sm max-w-none rounded-lg border p-3 mt-1 bg-gradient-to-br from-slate-50 to-indigo-50 text-xs overflow-x-auto shadow-sm">
                <div className="text-xs font-medium flex items-center mb-2" style={{ color: 'var(--primary)' }}>
                  <Brain className="h-3.5 w-3.5 mr-1.5" />
                  <span>Thinking process:</span>
                </div>
                <MessageProvider isLastMessage={isLastMessage}>
                  <MessageContent
                    content={effectiveThinkingContent}
                    isUser={false}
                    role="assistant"
                    className="text-xs"
                    // No streaming for thinking content
                    streaming={false}
                  />
                </MessageProvider>
              </div>
            </div>
          </div>
        )}


        {/* Export Panel (conditionally rendered) */}
        {canExport && isExportOpen && (
          <div
            id={`export-panel-${messageId}`} // ID for aria-controls
            className={`mt-2 p-3 border border-input rounded-md w-full ${isUserMessage ? 'ml-auto' : 'mr-auto'} max-w-[85%] bg-background shadow-sm`} // Style and align panel
          >
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              {/* Format Selection */}
              <div className="flex items-center flex-wrap gap-y-1"> {/* Allow wrapping */}
                <span className="text-foreground text-sm mr-2 font-medium shrink-0">Format:</span>
                <div className="flex border border-input rounded-md overflow-hidden shadow-sm">
                  {/* PDF Button */}
                  <button
                    onClick={() => handleFormatChange('pdf')}
                    className={`px-3 py-1 text-sm transition-colors ${selectedFormat === 'pdf' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-accent'}`}
                    aria-pressed={selectedFormat === 'pdf'} // Indicate selection state
                  >
                    PDF
                  </button>

                  {/* Word (DOCX) Button */}
                  <button
                    onClick={() => handleFormatChange('docx')}
                    className={`px-3 py-1 text-sm transition-colors border-l border-input ${selectedFormat === 'docx' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-accent'}`}
                     aria-pressed={selectedFormat === 'docx'}
                 >
                    Word
                  </button>

                  <button
                    onClick={() => handleFormatChange('pptx')}
                    className={`px-3 py-1 text-sm transition-colors border-l border-input ${selectedFormat === 'pptx' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-accent'}`}
                    aria-pressed={selectedFormat === 'pptx'}
                  >
                    PowerPoint
                  </button>

                  {/* HTML Button */}
                  <button
                    onClick={() => handleFormatChange('html')}
                    className={`px-3 py-1 text-sm transition-colors border-l border-input ${selectedFormat === 'html' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-accent'}`}
                    aria-pressed={selectedFormat === 'html'}
                  >
                    HTML
                  </button>
                </div>
              </div>

              {/* Quarto Export Button Component */}
              <div className="sm:ml-auto w-full sm:w-auto"> {/* Push button to right on larger screens */}
                <QuartoExportButton
                  message={{
                    ...message,
                    // Use clean content and include thinking content in export if available and shown
                    content: cleanContent + (hasThinkingContent && showThinking ?
                      `\n\n${effectiveThinkingContent}` : '')
                  }}
                  exportFormat={selectedFormat} // Pass the currently selected format
                  buttonText={`Export ${selectedFormat.toUpperCase()}`} // Dynamic button text
                  variant="button" // Use the standard button style
                  reportData={{ // Metadata to potentially include in the exported document
                    // Message and conversation metadata
                    timestamp: message.timestamp || Date.now(),
                    messageId: messageId,
                    senderName: name, // Name of the sender (e.g., "AI Assistant")
                    userId: currentUser?.uid, // ID of the logged-in user

                    // Document metadata (examples)
                    title: `Chat Export - ${new Date(message.timestamp || Date.now()).toLocaleString()}`,
                    author: currentUser?.displayName || currentUser?.email || 'User',
                    authorEmail: currentUser?.email || '',

                    // System metadata
                    exportDate: new Date().toISOString(),
                    exportTimestamp: Date.now(),
                    sourceApplication: 'AI Chat Interface', // Identify the source app

                    // URL information (useful for context)
                    chatUrl: typeof window !== 'undefined' ? window.location.href : '', // Current page URL
                    assistantContext: `Assistant ID: ${assistantId}`, // Include assistant ID

                    // Format-specific metadata (can be used by Quarto templates)
                    format: selectedFormat,
                    formatVersion: '1.0', // Example version
                    
                    // Include thinking status
                    includesThinking: hasThinkingContent && showThinking
                  }}
                  // Callback for successful export (receives backend result)
                  onSuccess={(result) => {
                     console.log('Export successful:', result);
                     // Optionally close panel on success:
                     // setIsExportOpen(false);
                  }}
                  // Callback for handling errors during export
                  onError={(error) => {
                    console.error('Export failed:', error);
                    // Potentially show a user-facing error message here
                  }}
                  // Callback specifically when download info is ready
                  onExportComplete={handleExportComplete}
                  // Pass className for potential styling overrides
                  className="w-full sm:w-auto" // Make button full width on small screens
                />
              </div>
            </div>
          </div>
        )}

        {/* Persistent Export Links Area (shows previously generated links) */}
        {canExport && (
          <div className={`w-full ${isUserMessage ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}>
            <QuartoExportLinks messageId={messageId} className="mt-2" />
          </div>
        )}

        {/* Structured Output Display */}
        {!isUserMessage && message.structuredOutput && (
          <div className={`w-full ${isUserMessage ? 'ml-auto' : 'mr-auto'} max-w-[85%]`}>
            <StructuredOutputDisplay 
              data={message.structuredOutput}
              messageId={messageId}
              className="mt-2"
            />
          </div>
        )}

      </div>
    </div>
  );
});

export default ChatMessageItem;